import { useEffect, useRef, useState } from "react";
import { Form } from "@dreambigger/design-system/src/components";
import {
  ResponseFields,
  SocureIdvStepAssets,
  Step,
} from "@dreambigger/shared/src/types";
import { StepProps } from "../pages/flows/[flowId]";
import styles from "./steps.module.scss";
import StepWrapper from "./step-wrapper";
import { useApplication } from "../api";
import isAuthError from "../utils/is-auth-error";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ReloadOutlined,
  PlayCircleFilled,
  Loading3QuartersOutlined,
} from "@ant-design/icons";
import { message, Spin } from "antd";
import { useBreakpoint } from "@dreambigger/design-system/src";
import {
  POLL_BACKOFF,
  afterEmptyHold,
  onAttention,
  startPolling,
} from "./socureIdvPolling";
import { resolveDelivery } from "./socureIdvDelivery";

const sessionUuidKey = "sessionUuid";
const statusKey = "status";

/**
 * A request WE cancelled, rather than one that failed.
 *
 * Both spellings are needed: axios reports its own cancellation as
 * `ERR_CANCELED`, while an `AbortSignal` firing at the fetch/XHR layer surfaces
 * as a DOMException named `AbortError`. Treating either as a polling failure
 * would log an error on every ordinary retry and unmount.
 */
const isAbortError = (error: unknown): boolean => {
  const code = (error as { code?: string; name?: string } | null)?.code;
  const name = (error as { name?: string } | null)?.name;
  return (
    code === "ERR_CANCELED" || name === "AbortError" || name === "CanceledError"
  );
};

enum Status {
  Pass = "pass",
  Fail = "fail",
  /**
   * A capture link was sent instead of running inline. Named `shareable` to match
   * the value Plaid's step stored (`plaid-idv.tsx`) — 224 live field mappings and
   * at least one transition rule read `<slug>__status`, and a cutover is meant to
   * be a prefix swap, not a vocabulary change. The member name says `Shareable`
   * for the same reason; only the delivery REQUEST still says "email".
   */
  Shareable = "shareable",
  /**
   * The document could not be read and Socure wants another capture. NOT a
   * failure: the data dictionary is explicit that reporting a needed recapture as
   * a rejection is wrong, and collapsing it into `Fail` left the applicant with no
   * way forward at all — the stored status short-circuits `initialize`, and the
   * server deliberately resumes a completed session, so neither a reload nor a
   * re-init could offer another attempt.
   */
  Retry = "retry",
  /**
   * Socure finished and a human decision is outstanding. Neutral, not a failure,
   * and deliberately not blocking: the applicant has done everything asked of
   * them, and the back office resolves it.
   */
  Review = "review",
}

/**
 * Session statuses that end the poll on the failure path.
 *
 * `completed` is handled on its own above — it is the only one that can carry a
 * decision. These three cannot progress and carry nothing: the evaluation
 * errored, the capture link expired unused, or the applicant declined consent.
 * Listed here rather than as a chain of `||` so adding a status to the API's
 * vocabulary and forgetting this file leaves an obvious gap.
 */
const TERMINAL_STATUSES = ["error", "expired", "declined"];

const spinnerIcon = <Loading3QuartersOutlined spin />;

/**
 * Socure IDV step: starts a RiskOS document_verification capture session for
 * the configured role and either renders the hosted capture link — as a QR code
 * to hand off to a phone on a wide viewport, or as a same-tab link on a narrow
 * one, where the phone *is* the only device — or emails it to the person
 * (deliverByEmail — in-branch flows). Completion is detected by polling the
 * session status endpoint; the terminal workflow decision maps to pass/fail
 * and is recorded in the step response fields ({ sessionUuid, status }) which
 * the IdvSocureEnricher later enhances from the canonical records.
 */
export default function SocureIdv({ flow, step, brand, progress }: StepProps) {
  const {
    slug,
    type,
    assets,
    assets: {
      role,
      helpText1,
      helpText2,
      errorText,
      allowContinueOnError,
      maskError,
    },
  }: Omit<Step, "assets"> & { assets: SocureIdvStepAssets } = step;

  /**
   * `email` is the only mode that hands the applicant off and stops the step;
   * `text` is an assist that keeps polling with the QR still on screen. Resolved
   * once here so every branch below asks the same question.
   */
  const delivery = resolveDelivery(assets);
  const deliverByEmail = delivery === "email";

  // state
  const [status, setStatus] = useState<string | null>(null);
  const [sessionUuid, setSessionUuid] = useState<string | null>(null);
  const [captureUrl, setCaptureUrl] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  /** Socure was asked to text this attempt's link. Not proof it arrived. */
  const [textRequested, setTextRequested] = useState(false);
  const [ready, setReady] = useState(false);
  const [disabled, setDisabled] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  /**
   * A start that failed, and can be tried again.
   *
   * Every failure path in `initialize` used to flash a toast and return, leaving
   * `ready` false — which renders the spinner — with the mount effect's
   * `initializeRef` already latched, so nothing would ever call it again. One
   * transient error at step entry stranded the applicant on an infinite spinner
   * until they reloaded the page. The retry path had an explicit re-invoke and
   * the plain mount path had none.
   */
  const [initError, setInitError] = useState<string | null>(null);
  const autoAdvance = useRef(false);
  const initializeRef = useRef<boolean>(false);

  // custom hooks
  const [form] = Form.useForm();
  const applicationHelper = useApplication(flow.financialInstitution.id);

  // Same breakpoint the Vouched step uses for its phone-handoff CTA (`dn
  // lg_db`): below `lg` there is no second device to hand the capture off to.
  const { isNarrowerThan } = useBreakpoint();
  const singleDevice = isNarrowerThan?.("lg") ?? false;

  /**
   * @param ignoreStoredStatus Skip the stored-outcome short-circuit below.
   * Set by `retryCapture`, which has just cleared that field — but reads it back
   * through the SWR data captured in ITS render, which still holds the outcome it
   * is trying to leave. Without this the first Take another photo bounced
   * straight back to the state it had just cleared.
   */
  const initialize = async (ignoreStoredStatus = false) => {
    setInitError(null);
    try {
      const response = ignoreStoredStatus
        ? undefined
        : applicationHelper.findResponseField(
            flow.id,
            "draft",
            slug,
            statusKey
          );
      // Non-empty, not merely "is a string". `retryCapture` clears this field to
      // start a fresh attempt, and an empty string satisfied `typeof` — so the
      // step short-circuited on its own cleared value, reported ready with no
      // session, and rendered nothing at all. Every real status is truthy.
      if (typeof response === "string" && response.length > 0) {
        setStatus(response);
        setReady(true);
        return;
      }

      const application = applicationHelper.find(flow.id, "draft");
      if (!application) {
        console.error("Socure IDV: no draft application for flow", flow.id);
        setInitError("We could not start your ID verification.");
        return;
      }

      const { data: session } = await applicationHelper.runSocureIdvSession(
        application.id,
        {
          role,
          delivery,
        }
      );

      if (!session?.sessionUuid) {
        setInitError("We could not start your ID verification.");
        return;
      }

      form.setFieldsValue({ [sessionUuidKey]: session.sessionUuid });
      setSessionUuid(session.sessionUuid);

      if (deliverByEmail) {
        // `emailSent` reports what the server actually did, not what was asked
        // of it — the send can fail on a Customer.io error or a person with no
        // reachable address. Storing Status.Shareable regardless told the applicant
        // to check an inbox nothing was sent to, and because a stored status
        // short-circuits `initialize` on every later mount, the send was never
        // reattempted either. Left unstored, the next attempt re-POSTs and the
        // server — which stamps an attempt only after a successful send — tries
        // again.
        if (session.emailSent === false) {
          setInitError("We could not email your verification link.");
          return;
        }
        // Awaited, like every other status write: an unawaited save that fails
        // leaves the spinner up with no status and no retry.
        const settled = await changeStatus(Status.Shareable);
        if (!settled) {
          setInitError("We could not email your verification link.");
        }
        return;
      }

      // `text` deliberately falls through to the inline rendering below.
      //
      // Unlike email it is not a hand-off: the applicant is still on the step,
      // so polling stays on and the QR / same-device link stays on screen. That
      // fallback is the whole reason to keep rendering it — Socure texts
      // whatever number is on file, and a stale or mistyped mobile is exactly
      // the failure `send_message` cannot detect, because the number is present
      // and well-formed. A false `textRequested` means no text was asked for at
      // all (no mobile on file), so the copy simply says nothing about one.
      setTextRequested(session.textRequested === true);

      setCaptureUrl(session.captureUrl ?? null);
      setQrCode(session.qrCode ?? null);
      setReady(true);
    } catch (error) {
      setInitError("We could not start your ID verification.");
      console.error("Error initializing ID Verification", error);
    }
  };

  /** Retry a failed start, from the block the failure rendered. */
  const retryInitialize = async () => {
    setInitError(null);
    setReady(false);
    await initialize(true);
  };

  const changeStatus = async (newStatus: string): Promise<boolean> => {
    const application = applicationHelper.find(flow.id, "draft");
    if (!application) {
      console.error(
        "Socure IDV: no draft application when saving status",
        newStatus
      );
      // A SAVE, not a start: two of this function's three callers run from the
      // polling outcome handlers, after the applicant has finished capture.
      //
      // Keyed because those callers deliberately keep polling when this returns
      // false, so an unkeyed toast stacks one per tick.
      message.error({
        content: "We could not save your verification result.",
        key: "socure-idv-save",
      });
      return false;
    }

    form.setFieldValue(statusKey, newStatus);

    const values = form.getFieldsValue();
    const filteredResponse: ResponseFields = {};
    Object.keys(values).forEach((key) => {
      if (values[key] === undefined) {
        return;
      }
      filteredResponse[key] = values[key];
    });

    const saved = await applicationHelper.update(application.id, {
      slug,
      type,
      fields: filteredResponse,
    });
    if (!saved) {
      return false;
    }

    autoAdvance.current = true;
    setStatus(newStatus);
    return true;
  };

  /**
   * Start a fresh capture after a `resubmit`.
   *
   * Both stored values have to go. The status short-circuits `initialize` on
   * mount, and the session uuid would otherwise be resumed — the server
   * deliberately hands back a completed session rather than minting a new one, so
   * leaving it in place would walk the applicant straight back to the outcome they
   * are trying to escape.
   */
  const retryCapture = async () => {
    const application = applicationHelper.find(flow.id, "draft");
    if (!application) {
      console.error("Socure IDV: no draft application on retry", flow.id);
      message.error("We could not start your ID verification.");
      return;
    }

    form.setFieldValue(statusKey, "");
    form.setFieldValue(sessionUuidKey, "");
    const saved = await applicationHelper.update(application.id, {
      slug,
      type,
      fields: {
        ...form.getFieldsValue(),
        [statusKey]: "",
        [sessionUuidKey]: "",
      },
    });
    if (!saved) {
      // `applicationHelper.update` has already shown "Unable to save
      // application". A second toast pointing at support, for what is usually a
      // transient failure, is the only thing this added.
      console.error("Socure IDV: could not clear stored status for retry");
      return;
    }

    // null, not "": the pending-capture block renders on `status === null`, so an
    // empty string cleared the old outcome without ever showing the new attempt.
    setStatus(null);
    setSessionUuid(null);
    setCaptureUrl(null);
    setQrCode(null);
    setReady(false);
    setDisabled(true);
    autoAdvance.current = false;
    // `initializeRef` deliberately stays true. Clearing it re-armed the mount
    // effect, which fires again when `isValidating` flips after the save — so a
    // second `initialize` raced this one, and the step showed the new QR and then
    // reverted as the loser finished. One initialize per retry, called here.
    await initialize(true);
  };

  // initialize when component mounts
  useEffect(() => {
    if (!applicationHelper.isValidating && !initializeRef.current) {
      initialize();
      initializeRef.current = true;
    }
  }, [applicationHelper.isValidating]);

  // Poll the session while the capture is pending (inline delivery only).
  useEffect(() => {
    if (!sessionUuid || status || deliverByEmail || sessionExpired) {
      return;
    }

    const application = applicationHelper.find(flow.id, "draft");
    if (!application) {
      return;
    }

    let stopped = false;
    let interval: ReturnType<typeof setTimeout> | undefined;
    const stop = () => {
      stopped = true;
      if (interval) {
        clearTimeout(interval);
      }
    };

    /**
     * Returns true when the poll settled the step, false when it came back with
     * nothing new — which is what decides whether the gap before the next hold
     * should start growing.
     */
    const poll = async (signal?: AbortSignal): Promise<boolean> => {
      if (stopped) {
        return false;
      }
      try {
        const { data: session } = await applicationHelper.getSocureIdvSession(
          application.id,
          sessionUuid,
          POLL_BACKOFF.holdSeconds,
          signal
        );
        // Re-checked AFTER the await, not only on entry. `retryCapture` tears
        // this effect down mid-request, and a response that resolved afterwards
        // wrote the OLD attempt's outcome through `changeStatus` — which saves
        // the form as it stands, by then holding the NEW attempt's uuid. A
        // superseded `resubmit` therefore flipped the fresh attempt straight back
        // to the retry screen.
        if (stopped) {
          return false;
        }
        // Normalized vocabulary, not Socure's: the endpoint returns
        // `status: "completed"` / `decision: "accept"`, never `eval_status` /
        // "ACCEPT" — those stay in the row's `custom` payload.
        if (session?.status === "completed") {
          // Each decision means a different thing to the applicant, and only
          // `reject` is a verification that failed.
          const outcomeFor = (decision?: string | null) => {
            if (decision === "accept") return Status.Pass;
            if (decision === "resubmit") return Status.Retry;
            if (decision === "review") return Status.Review;
            return Status.Fail;
          };
          // Awaited, and the poll is stopped only once the write lands.
          // `applicationHelper.update` returns false on any HTTP error and
          // `changeStatus` then early-returns, so stopping first meant a single
          // transient 5xx at the moment of completion left no timer, no state
          // change, and an applicant watching "Waiting for verification…" forever
          // on a verification that had actually succeeded. Left running, the next
          // tick simply tries again.
          const settled = await changeStatus(outcomeFor(session.decision));
          if (settled) {
            stop();
            return true;
          }
        } else if (
          session?.status &&
          TERMINAL_STATUSES.includes(session.status)
        ) {
          // These are terminal too — RiskOS failed the evaluation, the capture
          // link expired unused, or the applicant declined Socure's consent. None
          // can ever reach `completed`, so polling on would spin "Waiting for
          // verification…" forever. All three are a verification that did not
          // happen as far as this step is concerned, so they take the step's
          // configured failure treatment (errorText / maskError /
          // allowContinueOnError) rather than inventing a third outcome and a
          // third set of copy. The default message — "Please contact us to verify
          // your identity through another option." — is the right thing to say
          // for every one of them, and none of them claims the document was
          // rejected.
          // Settled before stopping, for the same reason as the `completed`
          // branch above: `changeStatus` returns false on any HTTP error, and
          // stopping first left no timer, no status and no way back — the
          // attention rescue bails on `stopped`. Left running, the next tick
          // tries again.
          const settled = await changeStatus(Status.Fail);
          if (settled) {
            stop();
            return true;
          }
        }
      } catch (error) {
        // We aborted this ourselves, so it is not a failure and there is nothing
        // to log. Checked before `isAuthError` because an aborted request has no
        // response to read a status off.
        if (isAbortError(error)) {
          return false;
        }
        // An expired access token is terminal, not transient: nothing refreshes
        // it while the applicant is away on their phone, so continuing to poll
        // would spin "Waiting for verification…" forever with no explanation.
        // The capture itself is safe — the session lives server-side and is
        // synced whenever it is next read.
        if (isAuthError(error)) {
          stop();
          setSessionExpired(true);
          return true;
        }
        // transient polling errors are retried on the next tick. A timeout here
        // is indistinguishable from one, and is handled the same way: the hold
        // is meant to return within its own window, so anything longer is a
        // network problem and the next hold simply tries again.
        //
        // Note the gap before that retry comes from how long this took, not from
        // the fact that it failed — a fast failure must not be met with the
        // hold-gap floor. See `degradedReturnRatio`.
        console.error("Error polling ID Verification status", error);
      }
      return false;
    };

    // Open a hold, and reopen it as soon as it comes back.
    //
    // Each `poll()` asks the server to keep the request open until the attempt
    // settles, so the loop below is not an interval — it is one long-lived
    // conversation. The delay between iterations is the GAP BETWEEN HOLDS, which
    // stays at the floor while a capture is plausibly still happening and only
    // decays once several holds in a row have come back with nothing (see
    // `socureIdvPolling.ts`). That is what makes the QR hand-off responsive
    // without leaving a parked tab hammering our API.
    //
    // `generation` is what makes an attention reset real. Bumping it retires the
    // chain currently running, so a pending timer cannot fire a poll and then
    // reschedule itself alongside the new chain.
    let state = startPolling();
    let attentionAtMs: number | null = Date.now();
    let generation = 0;
    // The request currently open, so it can be cancelled rather than merely
    // ignored. `clearTimeout` stops the next hold; it cannot stop an XHR that is
    // already in flight, and the generation counter only stops us from ACTING on
    // one. Without cancelling, alt-tabbing repeatedly inside a 20s window left a
    // dozen holds running server-side from a single tab, each doing its own
    // scoped re-reads for a response that would be discarded.
    let inFlight: AbortController | null = null;

    const abortInFlight = () => {
      inFlight?.abort();
      inFlight = null;
    };

    const runAndContinue = async (mine: number) => {
      const controller = new AbortController();
      inFlight = controller;
      // Measured around the request because the GAP depends on how long it took,
      // not on what it returned: a 20s hold that found nothing means reopen at
      // once, while an instant answer means something is not holding and the
      // floor would be a busy loop.
      const startedAt = Date.now();
      const settled = await poll(controller.signal);
      const elapsedMs = Date.now() - startedAt;
      if (inFlight === controller) {
        inFlight = null;
      }
      // Re-checked AFTER the await as well as before. `clearTimeout` cannot
      // cancel a timer that has already fired, so without this an attention
      // event landing mid-request leaves two chains running forever at double
      // the request rate — a regression of the very cost this schedule bounds.
      if (stopped || mine !== generation || settled) return;
      state = afterEmptyHold(state, elapsedMs);
      chain(mine);
    };

    const chain = (mine: number) => {
      if (stopped || mine !== generation) return;
      interval = setTimeout(() => {
        void runAndContinue(mine);
      }, state.delayMs);
    };
    chain(generation);

    // Reopen a hold immediately when the applicant's attention comes back.
    //
    // Worth being honest about what this does and does not buy. It does NOT
    // help the QR hand-off, which is the common case: there the applicant
    // finishes on their phone while this window stays open and focused, so
    // neither event ever fires. The long poll above is what covers that.
    //
    // It earns its place on the SAME-DEVICE path — below `lg` the capture opens
    // in a new tab, so coming back to this one is a real attention event and
    // mobile browsers may have suspended the timer entirely — and for anyone who
    // alt-tabs away from the QR screen and back. Both listeners are bound because
    // no single event covers both cases, and browsers disagree about which
    // fires; `onAttention` dedupes so one return costs one request.
    const onAttentionEvent = () => {
      if (stopped) return;
      const next = onAttention(state, Date.now(), attentionAtMs);
      state = next.state;
      attentionAtMs = next.attentionAtMs;
      if (!next.pollNow) return;
      generation += 1;
      if (interval) clearTimeout(interval);
      // Retire the hold this supersedes on the SERVER too, not just here.
      abortInFlight();
      void runAndContinue(generation);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        onAttentionEvent();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onAttentionEvent);

    return () => {
      stop();
      // Unmounting or retrying leaves a 20s hold running server-side otherwise.
      // `retryCapture` tears this effect down mid-request by design, so this is
      // the common path, not an edge case.
      abortInFlight();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onAttentionEvent);
    };
  }, [sessionUuid, status, deliverByEmail, sessionExpired]);

  // enable navigation on terminal/email states
  useEffect(() => {
    if (!status) {
      return;
    }

    setReady(true);

    const canProceed =
      status === Status.Pass ||
      status === Status.Shareable ||
      status === Status.Review ||
      (status === Status.Fail && allowContinueOnError);

    if (canProceed) {
      setDisabled(false);

      // Auto-advance ONLY on a pass, because that is the only outcome with
      // nothing for the applicant to read. Every other state carries information
      // they act on — "check your email for the link", "we have your ID and are
      // reviewing it", or the failure text — and advancing on their behalf flashed
      // the message past them and landed them on the next step wondering what
      // happened. They can still continue immediately; the button is enabled.
      if (autoAdvance.current && status === Status.Pass) {
        form.submit();
      }
    }
  }, [status, allowContinueOnError, form]);

  const wrapperProps = {
    // default the submit label — an unset submitButtonText renders an
    // icon-only button, which reads as an empty gray pill while disabled
    step: {
      ...step,
      assets: {
        ...step.assets,
        submitButtonText: step.assets.submitButtonText ?? "Continue",
      },
    },
    brand,
    flow,
    progress,
    form,
    disabled,
    // A failed start is not still working: leaving `processing` true kept the
    // step's own button spinning next to a "Try again" link.
    processing: !ready && !initError,
  };

  return (
    <StepWrapper {...wrapperProps}>
      <Form.Item name={sessionUuidKey} hidden={true} />
      <Form.Item name={statusKey} hidden={true} />

      {!ready && !status && !initError && <Spin indicator={spinnerIcon} />}

      {/* Outside the `(ready || !!status)` group deliberately: a start that
          failed has neither, and the spinner it replaces is what the applicant
          would otherwise watch forever. */}
      {!!initError && (
        <div className="flex flex-column items-center pt-4">
          <div className="flex items-center lh-2" style={{ color: "red" }}>
            <CloseCircleFilled className="f-7" />
            <div className="flex flex-column ml-2">
              <p className="mb-0 f-7">{initError}</p>
              <p className="fwb mb-0">
                Nothing you have already entered is lost.
              </p>
            </div>
          </div>
          <p className="f-4 gray-8 lh-4 mt-2 mb-2 tc">
            <a
              role="button"
              tabIndex={0}
              onClick={retryInitialize}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  retryInitialize();
                }
              }}
            >
              Try again
            </a>
          </p>
        </div>
      )}

      {(ready || !!status) && (
        <>
          {helpText1 && (
            <div>
              <p
                className={`${styles.textLinks} f-4 gray-8 lh-4`}
                dangerouslySetInnerHTML={{
                  __html: helpText1 ?? "",
                }}
              />
            </div>
          )}

          <div className={`flex flex-column justify-center items-center pt-4`}>
            {status === null && sessionExpired && (
              <div className="flex flex-column items-center mb-7">
                <div
                  className="flex items-center lh-2"
                  style={{ color: "red" }}
                >
                  <CloseCircleFilled className="f-7" />
                  <div className="flex flex-column ml-2">
                    <p className="mb-0 f-7">Your session timed out</p>
                    <p className="fwb mb-0">
                      Please refresh this page to continue. If you already
                      finished verifying on your phone, it has been saved — you
                      will not need to do it again.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {status === null && !sessionExpired && captureUrl && (
              // Pending capture. Two branches, because the handoff is only a
              // handoff when there are two devices.
              <div className="flex flex-column items-center mb-7">
                {textRequested && (
                  // Additive, above whichever branch renders below — the QR and
                  // the same-device link both stay. Worded as "we've sent" and
                  // never "check your phone": Socure returns no delivery
                  // receipt, so claiming arrival would be a promise we cannot
                  // keep, and the on-screen link has to remain the obvious way
                  // through for anyone whose number is wrong.
                  <p className="f-4 gray-8 lh-4 mb-3 tc">
                    We&apos;ve sent a link to your mobile phone. You can use it,
                    or continue below.
                  </p>
                )}
                {singleDevice ? (
                  // One device: no QR, since it cannot be scanned by the phone
                  // displaying it.
                  //
                  // A NEW TAB, not in-place navigation, and the reason is the
                  // camera. This flow is routinely embedded — `packages/embeds`
                  // is iframe content on the institution's own page — so
                  // navigating in place points the IFRAME at the capture app,
                  // and an iframe only gets camera access if the embedding page
                  // grants it with `allow="camera"`. That attribute lives in the
                  // institution's embed snippet, not in anything we control, so
                  // in-place navigation reliably produces a capture screen that
                  // cannot photograph anything. `target="_blank"` opens a
                  // top-level context, where the camera prompt is the browser's
                  // ordinary one.
                  //
                  // What made a new tab dangerous before was that backgrounding
                  // this tab stalled the only poll that advances the step. The
                  // `visibilitychange` and `focus` handlers above close that:
                  // returning to this tab — which is what closing the capture
                  // tab does — polls immediately, whether the return registers
                  // as the tab becoming visible or the window regaining focus.
                  // So the applicant is never told to fight Socure's terminal
                  // screen ("a representative will reach out") with a back
                  // button.
                  <p className="f-4 gray-8 lh-4 mt-2 mb-2 tc">
                    <a href={captureUrl} target="_blank" rel="noreferrer">
                      Verify your identity on this device
                    </a>
                    . It opens in a new tab — close it when you are done and you
                    will come straight back here to finish your application.
                  </p>
                ) : (
                  <>
                    {qrCode && (
                      <img
                        src={qrCode}
                        alt="Scan to verify your identity"
                        style={{ maxWidth: 240 }}
                      />
                    )}
                    <p className="f-4 gray-8 lh-4 mt-2 mb-2 tc">
                      Scan the QR code with your phone to verify your identity,
                      or{" "}
                      <a href={captureUrl} target="_blank" rel="noreferrer">
                        open the verification on this device
                      </a>
                      .
                    </p>
                  </>
                )}
                {/* Separated from the instruction above it: the two say
                    different kinds of thing — one is what to do, the other is
                    what we are waiting on — and butted together the status read
                    as another line of the sentence. */}
                <div className="flex items-center f-4 gray-7 mt-4">
                  <Spin indicator={spinnerIcon} />
                  <span className="ml-2">Waiting for verification…</span>
                </div>
              </div>
            )}
            {status === Status.Shareable && (
              <div style={{ color: "green" }}>
                <div className="flex items-center lh-2 mb-7">
                  <PlayCircleFilled className="f-7" />
                  <div className="flex flex-column ml-2">
                    <p className="mb-0 f-7">Verification In Progress</p>
                    <p className="fwb mb-0">
                      Please check your email for instructions on how to verify
                      your identity.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {(status === Status.Pass ||
              (status === Status.Fail && maskError)) && (
              <div
                className="flex items-center f-6 md_f-7 mb-7"
                style={{ color: "green" }}
              >
                <CheckCircleFilled />
                <p className="ml-2 mb-0">Verification Completed</p>
              </div>
            )}
            {status === Status.Retry && (
              <div style={{ color: "#e2832c" }}>
                {/* `items-start`, not `items-center`: this block is taller than
                    its siblings because it carries an action, and centring left
                    the icon floating beside the middle of the paragraph instead
                    of sitting with the heading it belongs to. */}
                <div className="flex items-start lh-2 mb-4">
                  <ReloadOutlined className="f-6 mt-1" />
                  <div className="flex flex-column ml-2">
                    <p className="mb-0 f-6">Another photo needed</p>
                    <p className="fwb mb-0">
                      We could not read your ID clearly enough. Nothing you have
                      already entered is lost.
                    </p>
                  </div>
                </div>
                {/* A text link, in the same treatment as the capture links
                    above — a solid button carried more weight than a recapture
                    deserves and read as another product's. `<a onClick>` is this
                    app's own idiom for a link-styled action (see
                    StripeIdentityFormItem, existing-member-shares); the keyboard
                    handling is added because an anchor without an href is not
                    focusable on its own. */}
                <p className="f-4 gray-8 lh-4 mt-2 mb-2 tc">
                  <a
                    role="button"
                    tabIndex={0}
                    onClick={retryCapture}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        retryCapture();
                      }
                    }}
                  >
                    Take another photo
                  </a>
                </p>
              </div>
            )}
            {status === Status.Review && (
              <div style={{ color: "green" }}>
                <div className="flex items-center lh-2 mb-7">
                  <CheckCircleFilled className="f-7" />
                  <div className="flex flex-column ml-2">
                    <p className="mb-0 f-7">ID received</p>
                    <p className="fwb mb-0">
                      We have your ID and are reviewing it. You can continue.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {status === Status.Fail && !maskError && (
              <div style={{ color: "red" }}>
                <div className="flex items-center lh-2 mb-7">
                  <CloseCircleFilled className="f-7" />
                  <div className="flex flex-column ml-2">
                    <p className="mb-0 f-7">
                      Verification Could Not Be Completed
                    </p>
                    <p className="fwb mb-0">
                      {errorText ||
                        "Please contact us to verify your identity through another option."}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {helpText2 && (
            <div>
              <p
                className={`${styles.textLinks} f-4 gray-8 lh-4 mb-6`}
                dangerouslySetInnerHTML={{
                  __html: helpText2,
                }}
              />
            </div>
          )}
        </>
      )}
    </StepWrapper>
  );
}
